import { useMemo } from 'react'
import { ItemHistoryQuery, OrderDirection, useItemHistoryQuery } from '../../../../graphql'
import { ItemHistoryEntry, ItemHistorySource, ItemHistorySourceType } from './types'

/**
 * Live data source for the Movement History table.
 *
 * Wraps the generated `useItemHistoryQuery` and maps its connection into the derivation
 * DTO (`ItemHistoryEntry`) that `deriveMovementRow` and the grid consume. This mapping is
 * the single seam between the wire shape and the view model — see `types.ts`.
 *
 * Fetches a bounded most-recent window (newest-first, so the running balance rolls back
 * from the current owned quantity). Real histories can run to thousands of rows;
 * `totalCount` reports the true total while `entries` holds the window, so the section can
 * say "most recent N of M". Server-side cursor pagination is the tracked follow-up.
 */
export const ITEM_HISTORY_WINDOW = 200

type QueryNode = ItemHistoryQuery['itemHistory']['edges'][number]['node']
type QuerySource = NonNullable<QueryNode['source']>
type QueryActor = QueryNode['modifiedByUser']

export type UseItemHistoryResult = {
	entries: ItemHistoryEntry[],
	totalCount: number,
	loading: boolean,
}

function mapSource( source: QuerySource | null | undefined ): ItemHistorySource | null {
	if ( !source ) {
		return null
	}
	switch ( source.__typename ) {
		case 'PurchaseOrder':
			// Pass the number through as-is (null when the federated lookup hasn't resolved it);
			// the derivation decides how to display a PO without a resolved number.
			return { __typename: 'PurchaseOrder', id: source.id, purchaseOrderNumber: source.purchaseOrderNumber ?? null }
		case 'ManualTransferOrder':
			return { __typename: 'ManualTransferOrder', id: source.id }
		case 'SystemTransferOrder':
			return { __typename: 'SystemTransferOrder', id: source.id }
		case 'InventoryLocation':
			return { __typename: 'InventoryLocation', id: source.id, name: source.name }
		case 'WorkOrderReference':
			return { __typename: 'WorkOrderReference', id: source.id }
		case 'OrderReference':
			return { __typename: 'OrderReference', id: source.id }
		case 'Asset':
			return { __typename: 'Asset', id: source.id }
		case 'ManualAdjustment':
			return { __typename: 'ManualAdjustment', id: source.id, reason: source.reasonDisplay ?? null }
		case 'Provider':
			return { __typename: 'Provider', name: source.name }
		case 'StorePart':
			return {
				__typename: 'StorePart',
				storePartId: String( source.storePartId ),
				isArchived: Boolean( source.isArchived ),
				partName: source.part?.name ?? source.part?.partNumber ?? null,
			}
		case 'CreditPurchaseReference':
			return { __typename: 'CreditPurchaseReference', id: source.id }
		case 'RetailSaleReference':
			return { __typename: 'RetailSaleReference', id: source.id }
		default:
			return null
	}
}

function mapActor( actor: QueryActor | null | undefined ): ItemHistoryEntry['modifiedByUser'] {
	if ( !actor ) {
		return null
	}
	return { firstName: actor.firstName, lastName: actor.lastName, email: actor.emailAddress }
}

function mapEntry( node: QueryNode ): ItemHistoryEntry {
	return {
		id: node.id,
		transactionId: node.transactionId,
		dateApplied: node.dateApplied,
		transactionStatus: node.transactionStatus,
		// Deliberately widened to the raw string so an unrecognised type degrades; see types.ts.
		transactionType: node.transactionType,
		quantityChange: node.quantityChange,
		quantityOrdered: node.quantityOrdered,
		quantityReceived: node.quantityReceived,
		costPerItem: node.costPerItem,
		itemStatus: node.itemStatus,
		memo: node.memo,
		customId: node.customId,
		// Enum string values are identical to the interim enum's; cast rather than re-declare.
		sourceType: node.sourceType as ItemHistorySourceType | null,
		sourceId: node.sourceId,
		source: mapSource( node.source ),
		modifiedByUser: mapActor( node.modifiedByUser ),
	}
}

export default function useItemHistory( itemId: string ): UseItemHistoryResult {
	const { data, loading } = useItemHistoryQuery( {
		variables: { itemId, paging: { first: ITEM_HISTORY_WINDOW }, order: OrderDirection.Desc },
	} )

	const entries = useMemo(
		() => ( data?.itemHistory.edges ?? [] ).map( ( edge ) => mapEntry( edge.node ) ),
		[ data ],
	)

	return { entries, totalCount: data?.itemHistory.totalCount ?? 0, loading }
}
